import { UserRole } from "@prisma/client";

import { FlatNavItem, SidebarNavItem } from "@/types/index";
import { VIDEO_SECTIONS, VIDEO_SUBSECTIONS } from "@/config/video-library";

export const flatNavLinks: FlatNavItem[] = [
  {
    title: "Wheel",
    href: "/dashboard/wheel",
    icon: "refresh",
    authorizeOnly: UserRole.USER,
    /*subItems: [
      {
        href: "/dashboard/wheel/find-deals",
        icon: "search",
        title: "Find Deals",
        authorizeOnly: UserRole.USER,
      },
      {
        href: "/dashboard/wheel/roll",
        icon: "refresh",
        title: "Roll",
        authorizeOnly: UserRole.USER,
      },
      {
        href: "/dashboard/wheel/covered-calls",
        icon: "trendingUp",
        title: "Covered Calls",
        authorizeOnly: UserRole.USER,
      },
    ],*/
  },
  {
    title: "Portfolio",
    href: "/dashboard/positions",
    icon: "briefcase",
    authorizeOnly: UserRole.USER,
  },
  {
    title: "Journal",
    href: "/dashboard/journal",
    icon: "bookOpen",
    authorizeOnly: UserRole.USER,
    isJournalSection: true,
    /*subItems: [
      {
        href: "/dashboard/journal?tab=overview",
        icon: "lineChart",
        title: "Profit View",
        authorizeOnly: UserRole.USER,
      },
      {
        href: "/dashboard/journal?tab=calendar",
        icon: "calendar",
        title: "Calendar",
        authorizeOnly: UserRole.USER,
      },
      {
        href: "/dashboard/journal?tab=summary",
        icon: "list",
        title: "Transactions",
        authorizeOnly: UserRole.USER,
      },
      {
        href: "/dashboard/journal?tab=performance",
        icon: "chart",
        title: "Per-Ticker",
        authorizeOnly: UserRole.USER,
      },
      {
        href: "/dashboard/journal/transactions",
        icon: "history",
        title: "Trades",
        authorizeOnly: UserRole.USER,
      },
    ],*/
  },
  {
    title: "GEX",
    href: "/dashboard/gex/gex-ai",
    icon: "activity",
    authorizeOnly: UserRole.USER,
    isGexSection: true,
    activePrefix: "/dashboard/gex",
    subItems: [
      {
        href: "/dashboard/gex/gex-ai",
        icon: "sparkles",
        title: "GEX AI",
        authorizeOnly: UserRole.USER,
      },
      {
        href: "/dashboard/gex/hub",
        icon: "radar",
        title: "Screener",
        authorizeOnly: UserRole.USER,
        tag: "Alpha",
      },
      {
        href: "/dashboard/gex/heatmap",
        icon: "grid3x3",
        title: "Heatmap",
        authorizeOnly: UserRole.USER,
      },
      {
        href: "/dashboard/gex/replay",
        icon: "play",
        title: "GEX Replay",
        authorizeOnly: UserRole.USER,
      },
      {
        href: "/dashboard/gex/gamma-flows",
        icon: "waves",
        title: "Options Flows",
        authorizeOnly: UserRole.USER,
        requiresGexPlan: true,
      },
      {
        href: "/dashboard/gex/workspace",
        icon: "layoutDashboard",
        title: "Workspace",
        authorizeOnly: UserRole.USER,
        requiresGexPlan: true,
      },
    ],
  },
  {
    title: "Copy Others",
    href: "/dashboard/copy-trading",
    icon: "users",
    authorizeOnly: UserRole.USER,
    subItems: [
      {
        href: "/dashboard/copy-trading/subscriptions",
        icon: "billing",
        title: "Subscriptions",
        authorizeOnly: UserRole.USER,
      },
      {
        href: "/dashboard/settings/trader",
        icon: "settings",
        title: "Virtual Hedge Fund",
        authorizeOnly: UserRole.USER,
      },
    ],
  },
  {
    title: "QW Intelligence",
    href: "/dashboard/options-gpt",
    icon: "sparkles",
    authorizeOnly: UserRole.USER,
    activePrefix: "/dashboard/options-gpt",
    subItems: [
      {
        href: "/dashboard/options-gpt",
        icon: "sparkles",
        title: "Chat",
        authorizeOnly: UserRole.USER,
      },
      {
        href: "/dashboard/options-gpt/trading-brain",
        icon: "brain",
        title: "Trading Brain",
        authorizeOnly: UserRole.USER,
        requiresPremiumUser: true,
      },
      {
        href: "/dashboard/options-gpt/jobs",
        icon: "clock",
        title: "Jobs",
        authorizeOnly: UserRole.USER,
      },
    ],
  },
];

// Video Library area: like the settings area, entering /dashboard/videos swaps
// the sidebar to a folder-style nav (sections + subsections) with a
// "Back to Dashboard" link. Publicly browsable, so no authorizeOnly.
export const VIDEOS_AREA_PREFIX = "/dashboard/videos";

const VIDEO_SECTION_ICONS: Record<string, FlatNavItem["icon"]> = {
  wheel: "refresh",
  "directional-trading": "activity",
  "agentic-trading": "bot",
};

const VIDEO_SUBSECTION_ICONS: Record<string, FlatNavItem["icon"]> = {
  intro: "play",
  "practical-examples": "target",
  "platform-deep-dives": "search",
};

export const videosNavLinks: FlatNavItem[] = [
  // Section item lands on the section hub (all subsections). Children deep-link
  // into a subsection URL but still show the full section playlist.
  ...VIDEO_SECTIONS.map((section, index): FlatNavItem => ({
    title: section.name,
    href: `/dashboard/videos/${section.slug}`,
    activePrefix: `/dashboard/videos/${section.slug}`,
    icon: VIDEO_SECTION_ICONS[section.slug] ?? "play",
    ...(index === 0 ? { sectionLabel: "SECTIONS" } : {}),
    subItems: section.comingSoon
      ? undefined
      : VIDEO_SUBSECTIONS.map((subsection) => ({
          title: subsection.name,
          href: `/dashboard/videos/${section.slug}/${subsection.slug}`,
          icon: VIDEO_SUBSECTION_ICONS[subsection.slug],
        })),
  })),
  {
    title: "All Videos",
    href: "/dashboard/videos",
    icon: "list",
    exactMatch: true,
    sectionLabel: "LIBRARY",
  },
];

export const SETTINGS_AREA_PREFIXES = [
  "/dashboard/settings",
  "/dashboard/preferences",
  "/dashboard/billing",
  "/dashboard/watchlists",
  "/dashboard/alerts",
  "/dashboard/broker-integration",
];

export const settingsNavLinks: FlatNavItem[] = [
  {
    title: "Connect Broker",
    href: "/dashboard/broker-integration",
    icon: "handshake",
    authorizeOnly: UserRole.USER,
    sectionLabel: "START USING",
  },
  {
    title: "Watchlists",
    href: "/dashboard/watchlists",
    icon: "watchlist",
    authorizeOnly: UserRole.USER,
  },
  {
    title: "Preferences",
    href: "/dashboard/settings?tab=preferences",
    icon: "slidersVertical",
    authorizeOnly: UserRole.USER,
  },
  {
    title: "Alerts",
    href: "/dashboard/alerts",
    icon: "bell",
    authorizeOnly: UserRole.USER,
    sectionLabel: "SETUP ALERTS",
  },
  {
    title: "Phone Alerts",
    href: "/dashboard/settings?tab=notifications",
    icon: "smartphone",
    authorizeOnly: UserRole.USER,
  },
  {
    title: "Account",
    href: "/dashboard/settings?tab=settings",
    icon: "settings",
    authorizeOnly: UserRole.USER,
    sectionLabel: "SETTINGS",
  },
  {
    title: "Billing",
    href: "/dashboard/settings/billing",
    icon: "billing",
    authorizeOnly: UserRole.USER,
  },
  {
    title: "CSV Import",
    href: "/dashboard/journal/imports",
    icon: "fileSpreadsheet",
    authorizeOnly: UserRole.USER,
  },
];

// Admin-only item shown at bottom of sidebar for special users
export const adminNavItem: FlatNavItem = {
  title: "Admin",
  href: "/dashboard/admin",
  icon: "eye",
};

// Legacy sidebarLinks kept for backward compatibility with any remaining consumers
export const sidebarLinks: SidebarNavItem[] = [];
